import { queryClient } from '@/shared/config/query-client.config';
import { ErrorInfo, isErrorInfo } from '@/shared/interfaces/error-info.interface';

/**
 * Reference data (brands, brand models, avatars) barely changes between page loads, but
 * every component that needs it calls the data layer directly on mount. Sharing one
 * React Query entry per payload collapses those duplicate requests into a single call.
 */
export const REFERENCE_DATA_STALE_TIME = 5 * 60 * 1000; // 5 minutes
export const REFERENCE_DATA_GC_TIME = 30 * 60 * 1000; // 30 minutes

interface CachedRequestConfig {
  staleTime?: number;
  gcTime?: number;
}

/**
 * Runs `request` through the shared QueryClient so concurrent callers with the same
 * `queryKey` await one in-flight request, and later callers reuse the cached result
 * until it goes stale.
 *
 * Data functions in this codebase return `ErrorInfo` as a value rather than throwing.
 * React Query has to see a rejection to keep a failure out of the cache, so the queryFn
 * throws it and we unwrap it again here — callers keep the exact same return contract.
 */
export async function cachedRequest<T>(
  queryKey: readonly unknown[],
  request: () => Promise<T | ErrorInfo>,
  config: CachedRequestConfig = {}
): Promise<T | ErrorInfo> {
  const { staleTime = REFERENCE_DATA_STALE_TIME, gcTime = REFERENCE_DATA_GC_TIME } = config;

  try {
    return await queryClient.fetchQuery({
      queryKey,
      queryFn: async () => {
        const result = await request();
        if (isErrorInfo(result)) {
          throw result;
        }
        return result;
      },
      staleTime,
      gcTime,
      // The data layer never retried before; keep failures as fast as they were.
      retry: false
    });
  } catch (error) {
    if (isErrorInfo(error)) {
      return error;
    }
    throw error;
  }
}

/** Drops every cache entry under `queryKey` so the next read refetches. */
export function invalidateCachedRequests(queryKey: readonly unknown[]): Promise<void> {
  return queryClient.invalidateQueries({ queryKey });
}
